/**
 * @fileoverview Employee information panel component showing comprehensive employee details.
 * Provides centralized view and control of employee settings, permissions, and assignments.
 */

import React, { useState } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { 
  faUser, 
  faEnvelope, 
  faDollarSign, 
  faBuilding,
  faUsers,
  faEye,
  faEyeSlash,
  faCheckCircle,
  faTimesCircle,
  faSave,
  faSpinner
} from '@fortawesome/free-solid-svg-icons';
import { fetchAuthSession } from 'aws-amplify/auth';
import { API_ENDPOINTS } from '../../config/api';
import { useAuth } from '../../contexts/AuthContext';
import './EmployeeInfoPanel.css';

/**
 * Employee information panel component for displaying and managing employee details.
 * Features:
 * - Basic employee information (name, email, ID, rate)
 * - Revenue visibility toggle (admin only)
 * - Assigned clients list
 * - Department and status information
 * - Real-time updates to backend
 * - Permission-based controls
 * 
 * @param {Object} props - Component properties
 * @param {Object} props.employee - Employee object with all details
 * @param {Function} props.onEmployeeUpdate - Callback when employee is updated
 * @returns {React.ReactElement} The employee info panel
 */
const EmployeeInfoPanel = ({ employee, onEmployeeUpdate, onStatusUpdate }) => {
  const { user } = useAuth();
  const [revenueVisibility, setRevenueVisibility] = useState(employee?.can_view_revenue || false);
  const [saving, setSaving] = useState(false);

  if (!employee) {
    return null;
  }

  const handleRevenueVisibilityToggle = async () => {
    const newValue = !revenueVisibility;
    setSaving(true);
    
    try {
      const { tokens } = await fetchAuthSession();
      const token = tokens.idToken.toString();
      
      const response = await fetch(
        API_ENDPOINTS.EMPLOYEES.UPDATE_REVENUE_VISIBILITY(employee.user_id),
        {
          method: 'POST',
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json'
          },
          body: JSON.stringify({
            can_view_revenue: newValue
          })
        }
      );

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.detail || 'Failed to update revenue visibility');
      }

      // Update local state
      setRevenueVisibility(newValue);
      
      // Notify parent component
      if (onEmployeeUpdate) {
        onEmployeeUpdate({
          ...employee,
          can_view_revenue: newValue
        });
      }
      
      // Show success status
      if (onStatusUpdate) {
        onStatusUpdate('success', `Revenue access ${newValue ? 'enabled' : 'disabled'} for ${employee.first_name}`);
      }

      console.log(`Revenue visibility ${newValue ? 'enabled' : 'disabled'} for ${employee.first_name}`);
    } catch (error) {
      console.error('Error updating revenue visibility:', error);
      
      // Show error status
      if (onStatusUpdate) {
        onStatusUpdate('error', error.message || 'Failed to update revenue visibility. Please try again.');
      }
    } finally {
      setSaving(false);
    }
  };

  const isAdmin = user?.isAdmin || user?.groups?.map(g => g.toLowerCase()).includes('admin');

  return (
    <div className="employee-info-panel">
      <div className="panel-header">
        <FontAwesomeIcon icon={faUser} />
        <h3>Employee Information</h3>
      </div>

      <div className="panel-content">
        {/* Basic Information Section */}
        <div className="info-section">
          <h4>Basic Information</h4>
          
          <div className="info-grid">
            <div className="info-item">
              <div className="info-icon">
                <FontAwesomeIcon icon={faUser} />
              </div>
              <div className="info-details">
                <span className="info-label">Name</span>
                <span className="info-value">{employee.first_name} {employee.last_name}</span>
              </div>
            </div>

            <div className="info-item">
              <div className="info-icon">
                <FontAwesomeIcon icon={faEnvelope} />
              </div>
              <div className="info-details">
                <span className="info-label">Email</span>
                <span className="info-value">{employee.email}</span>
              </div>
            </div>

            <div className="info-item">
              <div className="info-icon">
                <FontAwesomeIcon icon={faUser} />
              </div>
              <div className="info-details">
                <span className="info-label">User ID</span>
                <span className="info-value">{employee.user_id}</span>
              </div>
            </div>

            {employee.rate && (
              <div className="info-item">
                <div className="info-icon">
                  <FontAwesomeIcon icon={faDollarSign} />
                </div>
                <div className="info-details">
                  <span className="info-label">Hourly Rate</span>
                  <span className="info-value">${employee.rate}/hr</span>
                </div>
              </div>
            )}

            {employee.department && (
              <div className="info-item">
                <div className="info-icon">
                  <FontAwesomeIcon icon={faBuilding} />
                </div>
                <div className="info-details">
                  <span className="info-label">Department</span>
                  <span className="info-value">{employee.department}</span>
                </div>
              </div>
            )}

            <div className="info-item">
              <div className="info-icon">
                <FontAwesomeIcon 
                  icon={employee.active ? faCheckCircle : faTimesCircle} 
                  className={employee.active ? 'status-active' : 'status-inactive'}
                />
              </div>
              <div className="info-details">
                <span className="info-label">Status</span>
                <span className={`info-value status-badge ${employee.active ? 'active' : 'inactive'}`}>
                  {employee.active ? 'Active' : 'Inactive'}
                </span>
              </div>
            </div>

            {/* Revenue Visibility Toggle - Only for Admins */}
            {isAdmin && (
              <div className="info-item">
                <div className="info-icon">
                  <FontAwesomeIcon 
                    icon={revenueVisibility ? faEye : faEyeSlash}
                    className={revenueVisibility ? 'status-active' : 'status-inactive'}
                  />
                </div>
                <div className="info-details">
                  <span className="info-label">Revenue Access</span>
                  <button 
                    className={`revenue-toggle-btn ${revenueVisibility ? 'enabled' : 'disabled'}`}
                    onClick={handleRevenueVisibilityToggle}
                    disabled={saving}
                  >
                    {saving ? (
                      <>
                        <FontAwesomeIcon icon={faSpinner} spin />
                        <span>Saving...</span>
                      </>
                    ) : (
                      <>
                        <FontAwesomeIcon icon={revenueVisibility ? faEye : faEyeSlash} />
                        <span>{revenueVisibility ? 'Enabled' : 'Disabled'}</span>
                      </>
                    )}
                  </button>
                </div>
              </div>
            )}
          </div>
        </div>

        {/* Assigned Clients Section */}
        <div className="info-section">
          <h4>
            <FontAwesomeIcon icon={faUsers} />
            Assigned Clients
            {employee.clients && employee.clients.length > 0 && (
              <span className="client-count">{employee.clients.length}</span>
            )}
          </h4>
          
          {employee.clients && employee.clients.length > 0 ? (
            <div className="clients-list">
              {employee.clients.map((clientId, index) => (
                <div key={index} className="client-chip">
                  {clientId}
                </div>
              ))}
            </div>
          ) : (
            <div className="no-clients">
              <span>No clients assigned</span>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default EmployeeInfoPanel;

